import { AlertTriangle, ArrowRight, BookOpen, CheckCircle2 } from "lucide-react";
import { BottomSheet } from "@/components/app/bottom-sheet";
import { findPrereqChapter } from "@/lib/prereq/selectors";
import type { ChapterPrerequisite } from "@/lib/prereq/types";

/** Renders the supplied prerequisite data verbatim. Nothing is generated. */
export function PrereqSheet({
  chapter,
  chapters,
  onClose,
  onOpenChapter,
}: {
  chapter: ChapterPrerequisite | null;
  /** All chapters of the same exam + subject, used to resolve prerequisite links. */
  chapters: ChapterPrerequisite[];
  onClose: () => void;
  onOpenChapter: (chapter: ChapterPrerequisite) => void;
}) {
  const mandatory = chapter?.mandatoryChapters ?? [];
  const skills = chapter?.coreSkills ?? [];
  const warning = chapter?.commonMistakeWarning?.trim() ?? "";

  return (
    <BottomSheet
      open={Boolean(chapter)}
      onClose={onClose}
      title={chapter?.chapter ?? ""}
      subtitle={chapter ? `Class ${chapter.classLevel}` : undefined}
      tall
    >
      {chapter ? (
        <div className="space-y-4 pb-2">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-full border border-white/10 bg-surface px-2.5 py-1 text-[11px] font-semibold">
              Class {chapter.classLevel}
            </span>
            {chapter.difficultyRating ? (
              <span className="rounded-full border border-amber-400/25 bg-amber-400/10 px-2.5 py-1 text-[11px] font-bold text-amber-600 dark:text-amber-300">
                Difficulty: {chapter.difficultyRating}
              </span>
            ) : null}
          </div>

          <div>
            <h3 className="text-[13px] font-black uppercase tracking-[0.14em] text-muted-foreground">
              Before you start
            </h3>
            <p className="mt-1 text-[12.5px] leading-relaxed text-muted-foreground">
              Make sure you are comfortable with these foundations before starting this chapter.
            </p>
          </div>

          <section className="rounded-3xl border border-white/10 bg-card/60 p-4">
            <p className="flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.12em] text-muted-foreground">
              <BookOpen className="size-3.5" /> Mandatory chapters
            </p>
            {mandatory.length ? (
              <div className="mt-3 grid gap-2 sm:grid-cols-2">
                {mandatory.map((reference, i) => {
                  const match = findPrereqChapter(reference, chapters);
                  return (
                    <div
                      key={`${reference}-${i}`}
                      className="rounded-2xl border border-white/10 bg-surface/60 p-3"
                    >
                      <p className="text-[13px] font-semibold leading-snug">
                        {match?.chapter ?? reference}
                      </p>
                      <p className="mt-0.5 text-[11.5px] text-muted-foreground">
                        Required before starting this chapter
                      </p>
                      {match ? (
                        <button
                          type="button"
                          onClick={() => onOpenChapter(match)}
                          className="mt-2 inline-flex items-center gap-1 rounded-full border border-primary/30 bg-primary/10 px-3 py-1.5 text-[11.5px] font-bold text-primary transition-transform active:scale-95"
                        >
                          Review Chapter <ArrowRight className="size-3.5" />
                        </button>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="mt-2 text-[12.5px] text-muted-foreground">
                No mandatory prerequisite chapters.
              </p>
            )}
          </section>

          {skills.length ? (
            <section className="rounded-3xl border border-white/10 bg-card/60 p-4">
              <p className="text-[12px] font-bold uppercase tracking-[0.12em] text-muted-foreground">
                Core skills
              </p>
              <ul className="mt-2.5 space-y-2">
                {skills.map((skill, i) => (
                  <li key={`${skill}-${i}`} className="flex gap-2 text-[13px] leading-relaxed">
                    <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-emerald-500 dark:text-emerald-300" />
                    <span>{skill}</span>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          {warning ? (
            <section className="rounded-3xl border border-amber-400/30 bg-amber-400/8 p-4">
              <p className="flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.12em] text-amber-600 dark:text-amber-300">
                <AlertTriangle className="size-3.5" /> Common mistake
              </p>
              <p className="mt-1.5 text-[13px] leading-relaxed">{warning}</p>
            </section>
          ) : null}
        </div>
      ) : null}
    </BottomSheet>
  );
}
